import { Container, Divider } from "@material-ui/core";
import { Box, Grid } from "@mui/material";
import axios from "axios";
import {
  AutoCompleteWithSearch,
  ButtonGroupTop,
  CurrencyDisplay,
  CustomTable,
  DateRangePicker,
  DateTimeDisplay,
  ModalError,
  ModalSuccess,
  Page,
  PrimaryCloseButton,
  PrimaryRestoreButton,
  PrimaryViewButton,
  SearchTextInput,
  StatusChip,
} from "components";
import { HeaderTitle } from "layouts";
import { getTotalPage } from "lib";
import { debounce } from "lodash";
import moment from "moment-timezone";
import { useCallback, useEffect, useState } from "react";
import { useHistory } from "react-router";
import { hardBaseUrl } from "services/urlConstant";
import { globalStyles } from "styles";
import { getErrors, wordCapitalize } from "utils";
import ModalCancelPotentialClaim from "./Components/ModalCancelPotentialClaim";
import {
  MOCK_POTENTIAL_CLAIM_LOGS,
  MOCK_PUBLISHERS,
  MOCK_DSPS,
} from "./mockData";

const AdminPotentialClaim = () => {
  const classes = globalStyles();
  const history = useHistory();
  const token = localStorage.getItem("token");
  const headers = {
    Authorization: `Bearer ${token}`,
  };
  const pageTitle = "Potential Claim";

  const [loadingPage, setLoadingPage] = useState(true);
  const [loadingFilterPublisher, setLoadingFilterPublisher] = useState(false);
  const [loadingFilterDSP, setLoadingFilterDSP] = useState(false);

  const [queryParams, setQueryParams] = useState({
    page: 1,
    size: 10,
    search: "",
    publisher_id: "",
    dsp_id: "",
    start_date: null,
    end_date: null,
    sort: "",
  });

  const [filterParamsPublisher, setFilterParamsPublisher] = useState({
    page: 1,
    size: 10,
    search: "",
  });
  const [optionPublisher, setOptionPublisher] = useState(MOCK_PUBLISHERS);
  const [hasMorePublisher, setHasMorePublisher] = useState(false);

  const [filterParamsDSP, setFilterParamsDSP] = useState({
    page: 1,
    size: 10,
    search: "",
  });
  const [optionDSP, setOptionDSP] = useState(MOCK_DSPS);
  const [hasMoreDSP, setHasMoreDSP] = useState(false);

  const [dataTable, setDataTable] = useState(MOCK_POTENTIAL_CLAIM_LOGS);
  const [tablePageCount, setTablePageCount] = useState(1);

  // Modal Cancel State
  const [selectedCancelItem, setSelectedCancelItem] = useState(null);
  const [modalCancelOpen, setModalCancelOpen] = useState(false);

  const handleChangeQueryParams = (value, key) => {
    setQueryParams(currentState => ({
      ...currentState,
      ...(key !== "page" && { page: 1 }),
      [key]: value,
    }));
  };

  const handleChangeFilterParamsPublisher = (value, key) => {
    setFilterParamsPublisher(currentState => ({
      ...currentState,
      ...(key !== "page" && { page: 1 }),
      [key]: value,
    }));
  };

  const handleChangeFilterParamsDSP = (value, key) => {
    setFilterParamsDSP(currentState => ({
      ...currentState,
      ...(key !== "page" && { page: 1 }),
      [key]: value,
    }));
  };

  const getOptionPublisher = async () => {
    setLoadingFilterPublisher(true);
    try {
      const res = await axios.get(
        `${hardBaseUrl}/publisher`,
        {
          headers,
          params: filterParamsPublisher,
        }
      );
      const { data, meta } = res?.data || {};
      const rawList = Array.isArray(data) ? data : data?.data || [];
      const newOptions = rawList?.map(item => ({
        ...item,
        id: item?.publisher_id || item?.id,
        name: item?.publisher_name || item?.name || item?.sure_name,
      }));

      setOptionPublisher(prev => {
        const combined =
          filterParamsPublisher.page === 1
            ? newOptions
            : [...prev, ...newOptions];
        setHasMorePublisher(
          meta?.total ? meta?.total > combined?.length : false
        );
        return combined;
      });
    } catch (error) {
      ModalError(error?.response ? getErrors(error?.response) : error);
      setOptionPublisher([]);
      setHasMorePublisher(false);
    } finally {
      setLoadingFilterPublisher(false);
    }
  };

  const debounceFilterPublisher = useCallback(
    debounce(() => {
      getOptionPublisher();
    }, 500),
    [filterParamsPublisher]
  );

  const getOptionDSP = async () => {
    setLoadingFilterDSP(true);
    try {
      const res = await axios.get(`${hardBaseUrl}/admin/potential-claim/dsps`, {
        headers,
        params: filterParamsDSP,
      });
      const { data, meta } = res?.data || {};
      const rawList = Array.isArray(data) ? data : data?.data || [];
      const newOptions = rawList?.map(item => ({
        ...item,
        id: item?.dsp_id || item?.id,
        name: item?.dsp_name || item?.name,
      }));

      setOptionDSP(prev => {
        const combined =
          filterParamsDSP.page === 1
            ? newOptions
            : [...prev, ...newOptions];
        setHasMoreDSP(meta?.total ? meta?.total > combined?.length : false);
        return combined;
      });
    } catch (error) {
      ModalError(error?.response ? getErrors(error?.response) : error);
      setOptionDSP([]);
      setHasMoreDSP(false);
    } finally {
      setLoadingFilterDSP(false);
    }
  };

  const debounceFilterDSP = useCallback(
    debounce(() => {
      getOptionDSP();
    }, 500),
    [filterParamsDSP]
  );

  const getDataTable = async () => {
    setLoadingPage(true);
    try {
      const formattedStartDate = queryParams.start_date
        ? moment(queryParams.start_date).format("YYYY-MM-DD")
        : "";
      const formattedEndDate = queryParams.end_date
        ? moment(queryParams.end_date).format("YYYY-MM-DD")
        : "";

      const params = {
        page: queryParams.page,
        size: queryParams.size,
        search: queryParams.search,
        publisher_id: queryParams.publisher_id,
        dsp_id: queryParams.dsp_id,
        ...(formattedStartDate && { start_date: formattedStartDate }),
        ...(formattedEndDate && { end_date: formattedEndDate }),
        sort: queryParams.sort,
      };

      let res = await axios.get(
        `${hardBaseUrl}/admin/potential-claim/claim-logs`,
        {
          headers,
          params,
        }
      );
      const { data, meta } = res?.data || {};
      let listData = Array.isArray(data) ? data : data?.data || [];

      if (canceledIds && canceledIds.length > 0) {
        listData = listData.map(item =>
          canceledIds.includes(item.id) ? { ...item, status: "canceled" } : item
        );
      }

      setDataTable(listData);
      const pageCount = getTotalPage(meta?.total || listData.length, queryParams?.size);
      setTablePageCount(pageCount || 1);
    } catch (error) {
      ModalError(error?.response ? getErrors(error?.response) : error);
      setDataTable([]);
      setTablePageCount(1);
    } finally {
      setLoadingPage(false);
    }
  };

  const debounceDataTable = useCallback(
    debounce(() => {
      getDataTable();
    }, 500),
    [queryParams]
  );

  const handleRetryUnclaimProcess = async () => {
    setLoadingPage(true);
    try {
      ModalSuccess("Successfully Retry Potential Claim").then(() =>
        getDataTable()
      );
    } catch (error) {
      ModalError(getErrors(error?.response || error));
    } finally {
      setLoadingPage(false);
    }
  };

  const handleOpenCancelModal = item => {
    setSelectedCancelItem(item);
    setModalCancelOpen(true);
  };

  const [canceledIds, setCanceledIds] = useState([]);

  const handleCancelSuccess = () => {
    if (selectedCancelItem) {
      const cancelId = selectedCancelItem.id;
      setCanceledIds(prev => [...prev, cancelId]);
      setDataTable(prev =>
        prev.map(item =>
          item.id === cancelId ? { ...item, status: "canceled" } : item
        )
      );
    }
  };

  const canCancel = item => {
    const statusLower = (item?.status || "").trim().toLowerCase();
    const isFinished = statusLower === "finished" || statusLower === "finish";
    const isCcidSent =
      item?.is_ccid_sent === true ||
      item?.is_ccid_sent === "true" ||
      item?.is_ccid_sent === 1 ||
      item?.ccid_sent === true ||
      item?.ccid_sent === "true" ||
      item?.ccid_sent === 1 ||
      item?.is_sent_dsp === true ||
      item?.is_sent_dsp === "true" ||
      item?.is_sent_dsp === 1 ||
      item?.is_sent_to_dsp === true ||
      item?.is_sent_to_dsp === "true" ||
      item?.is_sent_to_dsp === 1;

    return isFinished && !isCcidSent;
  };

  useEffect(() => {
    const isSearching = Boolean(filterParamsPublisher?.search);
    if (isSearching) {
      debounceFilterPublisher();
      return () => debounceFilterPublisher.cancel();
    } else {
      getOptionPublisher();
    }
  }, [filterParamsPublisher]);

  useEffect(() => {
    const isSearching = Boolean(filterParamsDSP?.search);
    if (isSearching) {
      debounceFilterDSP();
      return () => debounceFilterDSP.cancel();
    } else {
      getOptionDSP();
    }
  }, [filterParamsDSP]);

  useEffect(() => {
    const isSearching = Boolean(queryParams?.search);
    if (isSearching) {
      debounceDataTable();
      return () => debounceDataTable.cancel();
    } else {
      getDataTable();
    }
  }, [queryParams]);

  const columnTable = [
    {
      name: "all",
      title: "Action",
      renderText: (cellVal, rowVal) => {
        const item =
          rowVal && typeof rowVal === "object"
            ? rowVal
            : cellVal && typeof cellVal === "object"
            ? cellVal
            : {};
        const isTimeoutOrError =
          item?.status === "error" || item?.status === "time-out";
        return (
          <Box display="flex" gap={0.5} alignItems="center">
            <PrimaryViewButton
              tooltipTitle="Detail Potential Claim"
              onClick={() =>
                history.push(
                  `/admin/potential-claim/log/${item?.id ||
                  item?.claim_report_id}`
                )
              }
            />
            <PrimaryRestoreButton
              tooltipTitle="Reload"
              disabled={!isTimeoutOrError}
              onClick={() => handleRetryUnclaimProcess(item?.id)}
            />
            <PrimaryCloseButton
              tooltipTitle={
                canCancel(item)
                  ? "Cancel Claim"
                  : "Cannot Cancel (Sent to DSP or not Finished)"
              }
              disabled={!canCancel(item)}
              onClick={() => handleOpenCancelModal(item)}
            />
          </Box>
        );
      },
    },
    {
      name: "document_number",
      title: "Doc No",
      useSort: true,
      sort_key: "document_number",
      renderText: (item, row) => item || row?.doc_no || "-",
    },
    {
      name: "publisher",
      title: "Publisher",
      useSort: true,
      sort_key: "publisher",
      renderText: (item, row) => item || row?.publisher_name || "-",
    },
    {
      name: "dsp_name",
      title: "DSP",
      useSort: true,
      sort_key: "dsp_name",
      renderText: (item, row) => item || row?.dsp || "-",
    },
    {
      name: "total_streams_claimed",
      title: "Total Traffic",
      useSort: true,
      sort_key: "total_traffic",
      headerAlign: "right",
      renderText: (item, row) => (
        <CurrencyDisplay value={item ?? row?.total_traffic ?? 0} />
      ),
    },
    {
      name: "revenue_claimed",
      title: "Total Revenue",
      useSort: true,
      sort_key: "total_revenue",
      headerAlign: "right",
      renderText: (item, row) => (
        <CurrencyDisplay value={item ?? row?.total_revenue ?? 0} prefix="Rp" />
      ),
    },
    {
      name: "start_date",
      title: "Start Date",
      renderText: item => (item ? <DateTimeDisplay date={item} /> : "-"),
    },
    {
      name: "end_date",
      title: "End Date",
      renderText: item => {
        if (!item) return "-";
        const dateObject = new Date(item);
        const isInvalidDate =
          isNaN(dateObject) || dateObject.getFullYear() < 2000;
        if (isInvalidDate) return "-";
        return <DateTimeDisplay date={item} />;
      },
    },
    {
      name: "status",
      title: "Status",
      renderText: status => {
        const st = (status || "").toLowerCase();
        return (
          <StatusChip
            type={
              st === "finished" || st === "finish"
                ? "success"
                : st === "canceled"
                ? "default"
                : st === "time-out" || st === "error"
                ? "danger"
                : st === "started"
                ? "warning"
                : ""
            }
            label={
              st === "time-out"
                ? "Request Time Out"
                : st === "finished" || st === "finish"
                ? "Finish"
                : wordCapitalize(status)
            }
          />
        );
      },
    },
  ];

  return (
    <Page className={classes?.root} title={pageTitle}>
      <Container maxWidth={false}>
        <HeaderTitle
          title={pageTitle}
          breadcrumbData={breadcrumbData({ pageTitle })}
        />
        <ButtonGroupTop
          items={[
            {
              id: "potential-claim-log-tab",
              title: "Potential Claim Log",
              link: "/admin/potential-claim",
            },
          ]}
        />
        <Divider className={classes?.flatDivider} />

        <Box my={2}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={6} md={3}>
              <SearchTextInput
                placeholder="Search"
                value={queryParams?.search}
                onChange={e =>
                  handleChangeQueryParams(e.target.value, "search")
                }
                disabled={loadingPage}
                width="100%"
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <AutoCompleteWithSearch
                placeholder="Publisher"
                options={optionPublisher}
                value={
                  optionPublisher?.find(
                    option => option?.id === Number(queryParams?.publisher_id)
                  ) || null
                }
                optionLabel="name"
                onChange={val => {
                  handleChangeQueryParams(val?.id || "", "publisher_id");
                }}
                inputValue={filterParamsPublisher?.search}
                isOptionEqualToValue={(option, value) =>
                  option?.id === value?.id
                }
                uniqueKey="id"
                onInputChange={e =>
                  handleChangeFilterParamsPublisher(
                    e?.target?.value || e,
                    "search"
                  )
                }
                setQueryParams={setFilterParamsPublisher}
                hasMore={hasMorePublisher}
                loading={loadingFilterPublisher}
                disabled={loadingPage}
                width="100%"
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <AutoCompleteWithSearch
                placeholder="DSP"
                options={optionDSP}
                value={
                  optionDSP?.find(
                    option => option?.id === Number(queryParams?.dsp_id)
                  ) || null
                }
                optionLabel="name"
                onChange={val => {
                  handleChangeQueryParams(val?.id || "", "dsp_id");
                }}
                inputValue={filterParamsDSP?.search}
                isOptionEqualToValue={(option, value) =>
                  option?.id === value?.id
                }
                uniqueKey="id"
                onInputChange={e =>
                  handleChangeFilterParamsDSP(e?.target?.value || e, "search")
                }
                setQueryParams={setFilterParamsDSP}
                hasMore={hasMoreDSP}
                loading={loadingFilterDSP}
                disabled={loadingPage}
                width="100%"
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <DateRangePicker
                placeholder="Period"
                startDate={queryParams?.start_date}
                endDate={queryParams?.end_date}
                handleChangeStartDate={date =>
                  handleChangeQueryParams(date, "start_date")
                }
                handleChangeEndDate={date =>
                  handleChangeQueryParams(date, "end_date")
                }
                disabled={loadingPage}
                width="100%"
              />
            </Grid>
          </Grid>
        </Box>

        <CustomTable
          isLoading={loadingPage}
          columnTable={columnTable}
          data={dataTable || []}
          page={queryParams?.page}
          pageSize={queryParams?.size}
          pageCount={tablePageCount}
          handleChangePage={(_, page) => handleChangeQueryParams(page, "page")}
          handleChangePageSize={e =>
            handleChangeQueryParams(e?.target?.value, "size")
          }
          sortValue={queryParams?.sort}
          onRequestSort={sortBy =>
            handleChangeQueryParams(
              `${sortBy} ${
                queryParams.sort === `${sortBy} asc` ? "desc" : "asc"
              }`,
              "sort"
            )
          }
          mt={2}
        />

        <ModalCancelPotentialClaim
          open={modalCancelOpen}
          onClose={() => {
            setModalCancelOpen(false);
            setSelectedCancelItem(null);
          }}
          dataItem={selectedCancelItem}
          onSuccess={handleCancelSuccess}
        />
      </Container>
    </Page>
  );
};

export const breadcrumbData = ({ pageTitle }) => [
  {
    label: "Home",
    link: "/admin/dashboard",
  },
  {
    label: "Song Claim",
    link: "/admin/potential-claim",
  },
  {
    label: pageTitle,
    active: true,
  },
];

export default AdminPotentialClaim;
